import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useParams, useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ASSET_PHOTO_PURPOSE, newId } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import {
  CAPABILITY_CODES, HOSTS, MOUNTABLE, POSITION_CODE, isOpen, rangeBounds, typeLabel, useAssetTypeLabels, useAssets, useCapabilities, useExternalIdentities, useMounts,
  type AssetRow,
} from '../data/fleet.ts'
import { formatLocal } from '../lib/time.ts'
import { BackLink, Banner, Field, Pill } from '../ui/components.tsx'
import { MeterPanel } from './MeterPanel.tsx'
import { CuttingSetupPanel } from './CuttingSetup.tsx'
import { RefuelForm } from './Refuel.tsx'
import { AssetCostsSection } from './AssetCosts.tsx'
import { History } from '../ui/History.tsx'
import { AvailabilityBanner, OutOfServicePill, SafetyActions, SafetyPanel } from './SafetyPanel.tsx'
import { AssetServiceSection } from './Service.tsx'
import { useAction, useWriteAccess } from '../ui/hooks.ts'
import type { Pending } from '../sync/merge.ts'
import { MachinePortrait, usePortrait } from '../photos/Portrait.tsx'
import { PhotoField } from '../photos/PhotoField.tsx'
import { usePhotoDraft } from '../photos/usePhotos.ts'
import { Picto } from '../ui/pictos.tsx'
import { scrollToHashWhenSettled } from '../ui/scrollToHash.ts'
import { Fold, TwoTap } from './workshop/shared.tsx'

const label = (a: Pick<AssetRow, 'readable_tag' | 'make' | 'model'>) => [a.readable_tag, [a.make, a.model].filter(Boolean).join(' ')].filter(Boolean).join(' — ')

/** A machine's page (M23 item 22). Two orders, one page: for whoever does not manage the register (the jardinier), the tag, the out-of-service
 *  banner and the three things to do (work with it, the pre-start check, a defect) come first, then the portrait and the facts, and the
 *  workshop's panels wait under one fold "Pour l'atelier"; for the office, the safety section comes right after the facts and each office panel is
 *  a fold, open only when it holds rows. On a wide screen the portrait, the facts, the meters and the service sit left, the safety and the
 *  orders right (`contents` on the two columns at phone width, where the `order` of the blocks decides). A `#section` in the address opens the
 *  page scrolled to that section (the workshop's cards, item 33). */
export function AssetDetailPage() {
  const { t, i18n } = useTranslation()
  const { assetId } = useParams({ from: '/assets/$assetId' })
  const hash = useRouterState({ select: (s) => s.location.hash })
  const assets = useAssets()
  const mounts = useMounts()
  const externals = useExternalIdentities()
  const capabilities = useCapabilities()
  const manage = useWriteAccess('asset.manage')
  const labels = useAssetTypeLabels(i18n.language)
  const a = assets?.find((x) => x.id === assetId)
  const ready = !!a && !!mounts
  // the section the address names, reached once the panels under it have rendered (not once at `ready`: the rows that render after push the section down)
  useEffect(() => (ready && hash ? scrollToHashWhenSettled(hash) : undefined), [ready, hash])
  if (!assets || !mounts) return <p>{t('common.loading')}</p>
  if (!a) return <section className="flex flex-col gap-3"><p>{t('assets.empty')}</p><Link to="/assets" className="btn">{t('common.back')}</Link></section>
  const retired = a.lifecycle === 'retired'
  const office = manage.allowed
  const facts: Array<[string, string | number | null | undefined]> = [
    [t('assets.kind'), t(`assets.kinds.${a.kind}`)], [t('assets.make'), a.make], [t('assets.model'), a.model],
    [t('assets.serial'), a.serial_no], [t('assets.year'), a.year], [t('assets.acquired'), a.acquired_on], [t('assets.assetType'), typeLabel(labels, a.asset_type_code)],
  ]
  const showMount = MOUNTABLE.includes(a.kind) || HOSTS.includes(a.kind)
  const mountRows = (mounts ?? []).some((m) => m.component_asset_id === a.id || (m.host_asset_id === a.id && isOpen(m)))
  const externalRows = (externals ?? []).some((r) => r.entity_id === a.id)
  const capabilityRows = (capabilities ?? []).some((r) => r.asset_id === a.id && isOpen(r))
  const column = 'contents lg:flex lg:flex-col lg:gap-6'
  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <BackLink to="/assets">← {t('common.back')}</BackLink>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-bold" data-testid="asset-tag">{a.readable_tag}</h1>
          {!retired && <OutOfServicePill assetId={a.id} />}
          {retired && <Pill tone="gray">{t('assets.retiredPill')}</Pill>}
          {a._pending && <Pill tone="amber">{t('assets.pending')}</Pill>}
        </div>
        <AvailabilityBanner assetId={a.id} />
        {a._pending && <Banner tone="warn">{t('assets.registeredOffline')}</Banner>}
        {retired && <Banner tone="info">{t('assets.retiredBanner')}</Banner>}
        {!office && <SafetyActions asset={a} />}
        {/* M15-A: "Faire le plein" under the three buttons (and for the office too): a 56-px row that opens the fill-up form on the machine itself, for whoever refuels (stock.record) */}
        {(a.kind === 'machine' || a.kind === 'vehicle') && !retired && !a._pending && <RefuelRow asset={a} />}
      </div>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]" data-testid="asset-columns" data-layout={office ? 'office' : 'field'}>
        <div className={column}>
          <div className="order-1 flex flex-col gap-3 lg:order-none">
            <PortraitPanel asset={a} retired={retired} />
            <dl className="card grid grid-cols-[auto_1fr] gap-x-4 gap-y-2" data-testid="asset-facts">
              {facts.filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => (<div key={k} className="contents"><dt className="font-semibold">{k}</dt><dd>{v}</dd></div>))}
            </dl>
            {office && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {!retired && <Link to="/assets/$assetId/edit" params={{ assetId: a.id }} className="btn" data-testid="edit-asset">{t('assets.edit')}</Link>}
                <Link to="/labels" search={{ ids: a.id }} className="btn" data-testid="label-link">{t('assets.printLabel')}</Link>
              </div>
            )}
          </div>
          <div className="order-3 lg:order-none"><MeterPanel asset={a} /></div>
          {/* M16-J: the measured cutting setup, for every asset (the club decides where it measures: per unit or per machine) */}
          <div className="order-4 lg:order-none"><CuttingSetupPanel asset={a} /></div>
          <div className="order-5 lg:order-none"><AssetServiceSection asset={a} /></div>
          <div className="order-7 lg:order-none"><History entityType='fleet.asset' entityId={a.id} /></div>
        </div>
        <div className={column}>
          <div className="order-2 lg:order-none"><SafetyPanel asset={a} actions={office} /></div>
          <div className="order-6 flex flex-col gap-3 lg:order-none" data-testid="office-panels">
            {office ? (
              <>
                {showMount && <Fold title={t('mounts.title')} help={MOUNTABLE.includes(a.kind) ? t('mounts.keepsHistory') : undefined} open={mountRows} testId="mount-panel"><MountPanel asset={a} /></Fold>}
                <Fold title={t('external.title')} help={t('external.help')} open={externalRows} testId="external-panel"><ExternalPanel asset={a} /></Fold>
                <Fold title={t('capability.title')} help={t('capability.help')} open={capabilityRows} testId="capability-panel"><CapabilityPanel asset={a} /></Fold>
                {!retired && <Fold title={t('assets.retire')} testId="retire-panel"><RetirePanel asset={a} /></Fold>}
              </>
            ) : (
              <Fold title={t('assets.forWorkshop')} testId="workshop-fold">
                {showMount && <><h3 className="text-lg font-bold">{t('mounts.title')}</h3><MountPanel asset={a} /></>}
                <h3 className="text-lg font-bold">{t('external.title')}</h3><ExternalPanel asset={a} />
                <h3 className="text-lg font-bold">{t('capability.title')}</h3><CapabilityPanel asset={a} />
              </Fold>
            )}
            {/* M15-E1: the machine's costs, for finance.read alone (read online behind the second factor, kept on no device); `#costs` opens it */}
            <AssetCostsSection asset={a} open={hash === 'costs'} />
          </div>
        </div>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------------------------------- the fill-up (M15-A)
/** The fold "Faire le plein" (`#refuel`: reached from the finish page once the page has settled). Offered to whoever holds stock.record, the workstation under a PIN included. */
function RefuelRow({ asset }: { asset: Pending<AssetRow> }) {
  const { t } = useTranslation()
  const access = useWriteAccess('stock.record')
  if (!access.permitted) return null
  return (
    <div id="refuel" className="scroll-mt-20">
      <Fold title={<span className="flex items-center gap-2"><Picto name="fuel" className="size-6 text-brand dark:text-fairway" />{t('stock.refuel.title')}</span>} testId="refuel-fold">
        <RefuelForm asset={asset} />
      </Fold>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------------- the machine's portrait (M17)
/** The newest photo of the machine, or its pictogram. Whoever takes photos may take a new one; the old ones stay (a photo is never deleted), the newest is the portrait.
 *  With no photo it is one row (M23 item 22): the pictogram and "Photographier la machine", not a 176-px empty frame. */
function PortraitPanel({ asset, retired }: { asset: Pending<AssetRow>; retired: boolean }) {
  const { t } = useTranslation()
  const { link, blob } = usePortrait(asset.id)
  const photo = useWriteAccess('attachment.add')
  const entity = useMemo(() => ({ entity_type: 'fleet.asset' as const, entity_id: asset.id, purpose: ASSET_PHOTO_PURPOSE }), [asset.id])
  const draft = usePhotoDraft(entity)
  const canTake = photo.permitted && !retired && !asset._pending
  if (!link) {
    return (
      <div className="flex items-center gap-3" data-testid="asset-portrait-panel" data-portrait="none">
        <MachinePortrait asset={asset} link={undefined} size="sm" />
        {canTake ? <div className="min-w-0 flex-1"><PhotoField draft={draft} disabled={!photo.allowed} label={t('assets.portraitTake')} /></div> : <p className="meta">{t('assets.portraitNone')}</p>}
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-2" data-testid="asset-portrait-panel" data-portrait="photo">
      <MachinePortrait asset={asset} link={link} blob={blob} size="lg" className="aspect-[4/3] w-full max-w-md rounded-2xl" />
      {link.caption && <p className="meta max-w-md break-words" data-testid="portrait-caption">{link.caption}</p>}
      {canTake &&<div className="max-w-md"><PhotoField draft={draft} disabled={!photo.allowed} label={t('assets.portraitChange')} /></div>}
      {blob && <p className="meta" data-testid="portrait-waiting">{t('assets.portraitWaiting')}</p>}
    </div>
  )
}

// ---------------------------------------------------------------------------------------------------- mounting
function MountPanel({ asset }: { asset: Pending<AssetRow> }) {
  const { t } = useTranslation()
  const { send, site } = useClub()
  const all = useAssets() ?? []
  const mounts = useMounts() ?? []
  const access = useWriteAccess('asset.manage')
  const { busy, error, run } = useAction()
  const mountId = useRef(newId())
  const [pick, setPick] = useState('')
  const [position, setPosition] = useState('main')
  const [flash, setFlash] = useState<string | null>(null)
  const byId = useMemo(() => new Map(all.map((x) => [x.id, x])), [all])
  const fmt = (d: Date | null) => (d ? formatLocal(d, site.timezone, 'dd/MM/yyyy') : '')
  const when = (m: { during: string }) => { const b = rangeBounds(m.during); return b.to ? t('common.fromTo', { from: fmt(b.from), to: fmt(b.to) }) : t('common.since', { when: fmt(b.from) }) }
  const active = asset.lifecycle !== 'retired'

  const isComponent = MOUNTABLE.includes(asset.kind)
  const isHost = HOSTS.includes(asset.kind)
  if (!isComponent && !isHost) return null

  const history = mounts.filter((m) => m.component_asset_id === asset.id).sort((x, y) => (rangeBounds(y.during).from?.getTime() ?? 0) - (rangeBounds(x.during).from?.getTime() ?? 0))
  const current = history.find((m) => isOpen(m))
  const carried = mounts.filter((m) => m.host_asset_id === asset.id && isOpen(m))
  const mountedElsewhere = (id: string) => mounts.find((m) => m.component_asset_id === id && isOpen(m))

  const doMount = (componentId: string, hostId: string) => run(async () => {
    if (!POSITION_CODE.test(position)) throw new Error(t('mounts.positionHelp'))
    await send('component.mount@2', { mount_id: mountId.current, component_id: componentId, host_id: hostId, position_code: position })
    mountId.current = newId(); setPick(''); setFlash(t('mounts.nowMounted'))
  })
  const doUnmount = (id: string) => run(async () => { await send('component.unmount@1', { mount_id: id }); setFlash(t('mounts.nowUnmounted')) })

  const hostChoices = all.filter((x) => HOSTS.includes(x.kind) && x.lifecycle !== 'retired' && x.id !== asset.id)
  const componentChoices = all.filter((x) => MOUNTABLE.includes(x.kind) && x.lifecycle !== 'retired' && x.id !== asset.id && mountedElsewhere(x.id)?.host_asset_id !== asset.id)
  const form = (choices: AssetRow[], noneText: string, onSubmit: (id: string) => void, labelText: string, submitText: string, testId: string) => (
    <form className="card flex flex-col gap-3" onSubmit={(e: FormEvent) => { e.preventDefault(); if (pick) onSubmit(pick) }}>
      {choices.length === 0 ? <p className="opacity-70">{noneText}</p> : (
        <>
          <Field label={labelText} htmlFor={`${testId}-pick`}>
            <select id={`${testId}-pick`} className="input" value={pick} onChange={(e) => setPick(e.target.value)} data-testid={`${testId}-pick`}>
              <option value="">{t('mounts.choose')}</option>
              {choices.map((c) => <option key={c.id} value={c.id}>{label(c)}</option>)}
            </select>
          </Field>
          <Field label={t('mounts.position')} hint={t('mounts.positionHelp')} htmlFor={`${testId}-pos`}>
            <input id={`${testId}-pos`} className="input" maxLength={30} value={position} onChange={(e) => setPosition(e.target.value)} />
          </Field>
          <button className="btn btn-primary" disabled={!pick || busy || !access.allowed} data-testid={`${testId}-go`}>{submitText}</button>
        </>
      )}
    </form>
  )
  // M23 item 36: unmounting takes two taps (the first turns the button into its confirmation)
  const unmountButton = (id: string) => <TwoTap label={t('mounts.unmount')} confirm={t('mounts.unmountConfirm')} disabled={busy} testId="unmount" onConfirm={() => void doUnmount(id)} />

  return (
    <div className="flex flex-col gap-3">
      {flash && <Banner tone="ok">{flash}</Banner>}
      {error && <Banner tone="error">{error}</Banner>}
      {isComponent && (
        <>
          {/* review of M23 item 1: the host's tag was a 22-px underlined text; the whole row opens the host's page (56 px), Démonter stands beside it */}
          <div className="card flex flex-col gap-2" data-testid="mounted-on">
            {current ? (
              <>
                <p className="meta font-semibold">{t('mounts.mountedOn')}</p>
                {byId.get(current.host_asset_id)
                  ? <Link to="/assets/$assetId" params={{ assetId: current.host_asset_id }} className="row-link -mx-2 !p-2 !shadow-none" data-testid="mounted-on-link">
                      <span className="flex min-w-0 flex-1 flex-col"><span className="text-lg font-bold">{byId.get(current.host_asset_id)!.readable_tag}</span><span className="meta">{t('mounts.at', { position: current.position_code })} · {when(current)}</span></span>
                      <Picto name="chevron" className="size-5 shrink-0 opacity-40" />
                    </Link>
                  : <p className="meta">… · {t('mounts.at', { position: current.position_code })} · {when(current)}</p>}
                {access.allowed && <div className="flex justify-end">{unmountButton(current.id)}</div>}
              </>
            ) : <p>{t('mounts.notMounted')}</p>}
          </div>
          {access.allowed && active && form(hostChoices, t('mounts.noHosts'), (h) => void doMount(asset.id, h), t('mounts.mountTo'), t('mounts.mount'), 'mount-host')}
          <h3 className="text-lg font-bold">{t('mounts.history')}</h3>
          {history.length === 0 ? <p className="opacity-70">{t('mounts.historyEmpty')}</p> : (
            <ul className="flex flex-col gap-2" data-testid="mount-history">
              {history.map((m) => (
                <li key={m.id} className="card flex justify-between gap-3">
                  <span className="font-semibold">{byId.get(m.host_asset_id)?.readable_tag ?? '…'} <span className="font-normal opacity-70">({m.position_code})</span></span>
                  <span className="meta">{when(m)}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {isHost && (
        <>
          <h3 className="text-lg font-bold">{t('mounts.carries')}</h3>
          {carried.length === 0 ? <p className="opacity-70">{t('mounts.carriesNothing')}</p> : (
            <ul className="flex flex-col gap-2" data-testid="carried">
              {carried.map((m) => (
                <li key={m.id} className="card flex flex-col gap-2">
                  {/* the whole row opens the component (56 px); Démonter is its own control beside it (review of M23 item 1: a 58×22 underlined tag was the last small control on the workshop's screens) */}
                  <Link to="/assets/$assetId" params={{ assetId: m.component_asset_id }} className="row-link -mx-2 !p-2 !shadow-none" data-testid="carried-link">
                    <span className="flex min-w-0 flex-1 flex-col"><span className="text-lg font-bold">{byId.get(m.component_asset_id)?.readable_tag ?? '…'}</span><span className="meta">{t('mounts.at', { position: m.position_code })} · {when(m)}</span></span>
                    <Picto name="chevron" className="size-5 shrink-0 opacity-40" />
                  </Link>
                  {access.allowed && <div className="flex justify-end">{unmountButton(m.id)}</div>}
                </li>
              ))}
            </ul>
          )}
          {access.allowed && active && form(componentChoices, t('mounts.noComponents'), (c) => void doMount(c, asset.id), t('mounts.component'), t('mounts.mount'), 'mount-comp')}
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------------------------------- external identities
function ExternalPanel({ asset }: { asset: Pending<AssetRow> }) {
  const { t } = useTranslation()
  const { send, site } = useClub()
  const rows = (useExternalIdentities() ?? []).filter((r) => r.entity_id === asset.id)
  const access = useWriteAccess('asset.manage')
  const { busy, error, run } = useAction()
  const mappingId = useRef(newId())
  const [f, setF] = useState({ provider: '', org: '', id: '' })
  const open = rows.filter((r) => isOpen(r))
  const closed = rows.filter((r) => !isOpen(r))
  return (
    <div className="flex flex-col gap-3">
      {open.length === 0 && <p className="opacity-70">{t('external.none')}</p>}
      <ul className="flex flex-col gap-2">
        {open.map((r) => (
          <li key={r.id} className="card flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-bold">{r.provider}{r.provider_org_ref ? ` · ${r.provider_org_ref}` : ''}</p>
              <p className="truncate font-mono text-sm">{r.external_id}</p>
              <p className="meta">{t('common.since', { when: rangeBounds(r.valid_during).from ? formatLocal(rangeBounds(r.valid_during).from!, site.timezone, 'dd/MM/yyyy') : '' })}</p>
            </div>
            {access.allowed && <TwoTap label={t('external.unmap')} confirm={t('external.unmapConfirm')} disabled={busy} testId="ext-unmap" className="!min-h-11" onConfirm={() => void run(async () => { await send('external_id.unmap@1', { mapping_id: r.id }) })} />}
          </li>
        ))}
        {closed.map((r) => (
          <li key={r.id} className="card flex justify-between gap-3 opacity-60">
            <span className="font-mono text-sm">{r.provider} · {r.external_id}</span>
            <span className="meta">{rangeBounds(r.valid_during).to ? t('common.fromTo', { from: formatLocal(rangeBounds(r.valid_during).from ?? 0, site.timezone, 'dd/MM/yyyy'), to: formatLocal(rangeBounds(r.valid_during).to!, site.timezone, 'dd/MM/yyyy') }) : ''}</span>
          </li>
        ))}
      </ul>
      {access.allowed && asset.lifecycle !== 'retired' && (
        <form className="card flex flex-col gap-3" onSubmit={(e) => {
          e.preventDefault()
          void run(async () => {
            await send('external_id.map@1', { mapping_id: mappingId.current, asset_id: asset.id, provider: f.provider.trim(), ...(f.org.trim() ? { provider_org_ref: f.org.trim() } : {}), external_id: f.id.trim() })
            mappingId.current = newId(); setF({ provider: '', org: '', id: '' })
          })
        }}>
          <Field label={t('external.provider')} hint={t('external.providerHelp')} htmlFor="ext-provider"><input id="ext-provider" className="input" required maxLength={31} value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} /></Field>
          <Field label={t('external.orgRef')} htmlFor="ext-org"><input id="ext-org" className="input" maxLength={120} value={f.org} onChange={(e) => setF({ ...f, org: e.target.value })} /></Field>
          <Field label={t('external.id')} htmlFor="ext-id"><input id="ext-id" className="input" required maxLength={120} value={f.id} onChange={(e) => setF({ ...f, id: e.target.value })} /></Field>
          {error && <Banner tone="error">{error}</Banner>}
          <button className="btn btn-primary" disabled={busy} data-testid="ext-add">{t('external.add')}</button>
        </form>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------------------------------- capabilities (read-only)
function CapabilityPanel({ asset }: { asset: Pending<AssetRow> }) {
  const { t } = useTranslation()
  const { send } = useClub()
  const rows = (useCapabilities() ?? []).filter((r) => r.asset_id === asset.id && isOpen(r))
  const access = useWriteAccess('asset.manage')
  const { busy, error, run } = useAction()
  const capId = useRef(newId())
  const [code, setCode] = useState('')
  const [provider, setProvider] = useState('')
  return (
    <div className="flex flex-col gap-3">
      {rows.length === 0 && <p className="opacity-70">{t('capability.none')}</p>}
      <ul className="flex flex-col gap-2">
        {rows.map((r) => (
          <li key={r.id} className="card flex justify-between gap-3">
            <span className="font-semibold">{t(`capability.codes.${r.capability_code}`)}</span>
            <span className="meta">{r.provider}</span>
          </li>
        ))}
      </ul>
      {access.allowed && asset.lifecycle !== 'retired' && (
        <form className="card flex flex-col gap-3" onSubmit={(e) => {
          e.preventDefault()
          void run(async () => { await send('capability.declare@1', { capability_id: capId.current, asset_id: asset.id, capability_code: code, provider: provider.trim() }); capId.current = newId(); setCode(''); setProvider('') })
        }}>
          <Field label={t('capability.code')} htmlFor="cap-code">
            <select id="cap-code" className="input" required value={code} onChange={(e) => setCode(e.target.value)}>
              <option value="">{t('capability.choose')}</option>
              {CAPABILITY_CODES.map((c) => <option key={c} value={c}>{t(`capability.codes.${c}`)}</option>)}
            </select>
          </Field>
          <Field label={t('capability.provider')} hint={t('external.providerHelp')} htmlFor="cap-provider"><input id="cap-provider" className="input" required maxLength={31} value={provider} onChange={(e) => setProvider(e.target.value)} /></Field>
          {error && <Banner tone="error">{error}</Banner>}
          <button className="btn btn-primary" disabled={busy || !code} data-testid="cap-add">{t('capability.declare')}</button>
        </form>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------------------------------- retire
function RetirePanel({ asset }: { asset: Pending<AssetRow> }) {
  const { t } = useTranslation()
  const { send } = useClub()
  const mounts = useMounts() ?? []
  const { busy, error, run } = useAction()
  const [ask, setAsk] = useState(false)
  const [reason, setReason] = useState('')
  // cosmetic: the server refuses too (ASSET_HAS_MOUNTS)
  const hasMounts = mounts.some((m) => isOpen(m) && (m.host_asset_id === asset.id || m.component_asset_id === asset.id))
  return (
    <div className="flex flex-col gap-3">
      {hasMounts && <Banner tone="warn">{t('assets.hasMounts')}</Banner>}
      {!ask ? <button className="btn" disabled={hasMounts} onClick={() => setAsk(true)} data-testid="retire">{t('assets.retire')}</button> : (
        <div className="card flex flex-col gap-3">
          <p>{t('assets.retireConfirm', { tag: asset.readable_tag })}</p>
          <Field label={t('assets.retireReason')} htmlFor="retire-reason"><input id="retire-reason" className="input" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
          {error && <Banner tone="error">{error}</Banner>}
          <div className="grid grid-cols-2 gap-3">
            <button className="btn" onClick={() => setAsk(false)}>{t('common.cancel')}</button>
            <button className="btn btn-primary" disabled={busy} data-testid="retire-confirm"
              onClick={() => void run(async () => { await send('asset.retire@1', { asset_id: asset.id, ...(reason.trim() ? { reason: reason.trim() } : {}) }); setAsk(false) })}>{t('assets.retireDo')}</button>
          </div>
        </div>
      )}
    </div>
  )
}
